import {
  Attachment,
  AttachmentMetadata,
  BotMessage,
  Chat,
  CustomerMessage,
} from "@/types/chat"
import { Collection } from "@/types/collection"
import { DayObject, DayRange, Timestamp } from "@/types/date"
import { EventActor } from "@/types/event-actor"
import { ExploreFilters, SocialFilters } from "@/types/explore"
import { FlightBooking } from "@/types/flights/flight-booking"
import { FlightContractUpgrade } from "@/types/flights/flight-contract-upgrade"
import { FlightItinerary } from "@/types/flights/flight-itinerary"
import {
  FlightItineraryApi,
  FlightItinerarySummary,
} from "@/types/flights/flight-itinerary-api"
import { FlightOption } from "@/types/flights/flight-option"
import { FlightSlice } from "@/types/flights/flight-slice"
import { FlightSliceApi } from "@/types/flights/flight-slice-api"
import {
  AvailableRoom,
  BookedHotelRooms,
  HotelBooking,
  HotelBookingSummary,
  HotelBookingVendorData,
  PriceDetails,
} from "@/types/hotels/hotels"
import {
  Inspiration,
  InspirationItem,
  InspirationWhere,
} from "@/types/inspiration"
import { ItineraryEvent } from "@/types/itinerary"
import { Notification } from "@/types/notification"
import { AirportSummary, Location, Place } from "@/types/place"
import {
  Budget,
  Room,
  StopDetails,
  TimeFrame,
  TravelerGroup,
  Trip,
  TripRef,
  TripSummary,
  TripVibe,
} from "@/types/trip"
import { CurrentUser } from "@/types/user"
import { Nullable } from "@/types/util"

import type { UndoRedoResponseAndMessage } from "@/lib/itinerary-action-types"
import { assert } from "@/lib/utils/assertions"
import { dayObjectAsUrlParam } from "../date-utils"
import { formatYearMonthDayHourMinSecTz } from "../format-utils"
import { fullLocationName } from "../location-utils"
import { FeedbackData } from "../message-api"
import {
  AmplitudeAirportSummaryProps,
  AmplitudeAttachmentMetaDataProps,
  AmplitudeAttachmentProps,
  AmplitudeAvailableRoomProps,
  AmplitudeBookedHotelRoomProps,
  AmplitudeBotMessageProps,
  AmplitudeBudgetProps,
  AmplitudeChatProps,
  AmplitudeCollectionProps,
  AmplitudeCommunityFilterProps,
  AmplitudeCustomerMessageProps,
  AmplitudeDateRangeProps,
  AmplitudeDayTimeProps,
  AmplitudeEmptyValue,
  AmplitudeEventActorProps,
  AmplitudeExploreFilterProps,
  AmplitudeFeedbackProps,
  AmplitudeFlightBookingProps,
  AmplitudeFlightItineraryProps,
  AmplitudeFlightLegsProps,
  AmplitudeFlightOptionProps,
  AmplitudeFlightPriceProps,
  AmplitudeFlightSliceApiProps,
  AmplitudeFlightSlicePriceProps,
  AmplitudeFlightSliceProps,
  AmplitudeFlightUpgradeProps,
  AmplitudeHotelBookingAndRoomProps,
  AmplitudeHotelBookingProps,
  AmplitudeHotelPriceDetails,
  AmplitudeInspirationItemProps,
  AmplitudeInspirationItemUpdateProps,
  AmplitudeInspirationProps,
  AmplitudeInspirationUpdateProps,
  AmplitudeItineraryEventProps,
  AmplitudeLocationProps,
  AmplitudeRedoResponseProps,
  AmplitudeRoomsProps,
  AmplitudeStopDetailsProps,
  AmplitudeTimeFrameProps,
  AmplitudeTravelersProps,
  AmplitudeTripProps,
  AmplitudeTripVibeProps,
  AmplitudeUndoResponseProps,
  AmplitudeUserClass,
  AmplitudeVendorDataProps,
  EventNotificationClickProperties,
} from "./amplitude-types"

// Developers: write your own conversion from you object
// to the appropriate properties that you want to send out
// with your event.
//
// For exampple, for "chat", I might want to only send out only the id
// for "trip", I might want to only send out trip id and trip name
export const timeStampToAmplitudeProperties = (
  timeStamp: Timestamp,
  timeZone: string
): AmplitudeDayTimeProps => {
  return {
    dateTime: formatYearMonthDayHourMinSecTz(timeStamp, timeZone),
  }
}

export const dayRangeToAmplitudeProperties = (
  dayRange: DayRange
): AmplitudeDateRangeProps => {
  return {
    startDate: `${dayRange.from ? dayObjectAsUrlParam(dayRange.from) : undefined}`,
    endDate: `${dayRange.to ? dayObjectAsUrlParam(dayRange.to) : undefined}`,
  }
}

export const timeFrameToAmplitudeProperties = (
  timeFrame: TimeFrame
): { timeFrame: AmplitudeTimeFrameProps } => {
  return {
    timeFrame: {
      startDate: `${timeFrame.start_date && dayObjectAsUrlParam(timeFrame.start_date)}`,
      duration: timeFrame.duration,
    },
  }
}

export const travelerGroupToAmplitudeProperties = (
  travelers: TravelerGroup
): { travelers: AmplitudeTravelersProps } => {
  return { travelers: travelers }
}

export const packageInputsToAmplitudeProperties = (
  service: string,
  rooms: Room[],
  carRental: boolean,
  location: Location | undefined,
  tripRef: TripRef
): {
  service: string
  rooms: AmplitudeRoomsProps
  carRental: boolean
  location: AmplitudeLocationProps | undefined
  tripRef: TripRef
} => {
  return {
    service: service,
    rooms: rooms,
    carRental: carRental,
    location: location
      ? locationToAmplitudeProperties(location).location
      : undefined,
    tripRef: tripRef,
  }
}

export const budgetToAmplitudeProperties = (
  budget: Budget
): { budget: AmplitudeBudgetProps } => {
  return { budget: budget }
}

export const tripVibeToAmplitudeProperties = (
  vibe: TripVibe
): { vibe: AmplitudeTripVibeProps } => {
  return { vibe }
}

export const chatToAmplitudeProperties = (
  chat: Chat
): { chat: AmplitudeChatProps } => {
  return {
    chat: {
      id: chat.id,
      ...(chat.vertical ? { vertical: chat.vertical } : {}),
    },
  }
}

export const tripToAmplitudeProperties = (
  trip: Trip | TripSummary
): { trip: AmplitudeTripProps } => {
  return {
    trip: {
      id: trip.id,
      name: trip.name,
      roadTrip: trip.roadtrip,
    },
  }
}

export const botMessageToAmplitudeProperties = (
  message: BotMessage
): { message: AmplitudeBotMessageProps } => {
  return {
    message: {
      id: message.id,
      intent: message.intent,
      source: "Chat",
    },
  }
}

export const customerMessageToAmplitudeProperties = (
  message: CustomerMessage
): { message: AmplitudeCustomerMessageProps } => {
  return {
    message: {
      id: message.id,
      question: message.body,
      source: "TripPlan",
    },
  }
}

export const placeToAmplitudeProperties = (
  place: Place
): { location: AmplitudeLocationProps } => {
  return locationToAmplitudeProperties(place)
}

// Same rule as the backend (Agent::Browser::SessionUtils#browser_session_tags): anon for anonymous
// users, mt for verified @mindtrip.ai accounts (is_mindtrip comes from User#mindtrip?), else non_mt.
export const userToAmplitudeUserClass = (
  user: CurrentUser
): AmplitudeUserClass => {
  if (user.provider === "anonymous") return "anon"
  return user.is_mindtrip ? "mt" : "non_mt"
}

export const locationToAmplitudeProperties = (
  location: Location | Place
): { location: AmplitudeLocationProps } => {
  return {
    location: {
      entityRef: location.entity_ref,
      name: location.name,
      type: location.type,
      city: location.city,
      state: location.state_code,
      countryCode: location.country_code,
      timeZone: location.time_zone,
      url: location.url,
      categories:
        location.type === "location"
          ? [location.category]
          : location.categories,
    },
  }
}

export const stopDetailsToAmplitudeProperties = (
  stopsDetails: StopDetails[]
): { stops: AmplitudeStopDetailsProps[] } => {
  return {
    stops: stopsDetails.map((stopDetails) => ({
      location: locationToAmplitudeProperties(stopDetails.location).location,
      days: stopDetails.days,
    })),
  }
}

export const hotelBookingSummaryToAmplitudeProperties = (
  hotelBookingSummary: HotelBookingSummary
): { hotelBooking: AmplitudeHotelBookingProps } => {
  const bookingRef = hotelBookingSummary.ref
    ? hotelBookingSummary.ref
    : undefined
  const bookingStatus = hotelBookingSummary.status
    ? hotelBookingSummary.status
    : undefined
  const tripRef = hotelBookingSummary.trip_ref
    ? hotelBookingSummary.trip_ref
    : undefined
  const tripName = hotelBookingSummary.trip_name
    ? hotelBookingSummary.trip_name
    : undefined
  const hotelRef = hotelBookingSummary.entity_ref
    ? hotelBookingSummary.entity_ref
    : undefined
  const entityName = hotelBookingSummary.entity_name
    ? hotelBookingSummary.entity_name
    : undefined
  const num_nights = hotelBookingSummary.properties.booking.num_nights
    ? hotelBookingSummary.properties.booking.num_nights
    : 1
  const num_rooms = hotelBookingSummary.properties.booking.num_rooms
    ? hotelBookingSummary.properties.booking.num_rooms
    : 1
  const num_guests = hotelBookingSummary.properties.booking.num_guests
    ? hotelBookingSummary.properties.booking.num_guests
    : 1

  return {
    hotelBooking: {
      ref: bookingRef,
      status: bookingStatus,
      provider_name: hotelBookingSummary.provider_name,
      tripRef: tripRef,
      tripName: tripName,
      placeRef: hotelRef,
      entityName: entityName,
      numNights: num_nights,
      numRooms: num_rooms,
      numGuests: num_guests,
    },
  }
}

export const hotelBookingToAmplitudeProperties = (
  hotelBooking: HotelBooking
): AmplitudeHotelBookingAndRoomProps => {
  const roomData =
    hotelBooking.vendor_data && Object.keys(hotelBooking.vendor_data).length > 0
      ? hotelBooking.vendor_data
      : hotelBooking.available_room
  return {
    ...hotelBookingSummaryToAmplitudeProperties(hotelBooking),
    ...roomDataToAmplitudeProperties(roomData),
  }
}

export const bookedHotelRoomsToAmplitudeProperties = (
  bookedHotelRooms: BookedHotelRooms
): { bookedHotelRooms: AmplitudeBookedHotelRoomProps } => {
  const totalRooms = bookedHotelRooms?.total_rooms
    ? bookedHotelRooms?.total_rooms
    : undefined
  return {
    bookedHotelRooms: {
      totalRooms: totalRooms,
    },
  }
}

export const flightItineraryApiToAmplitudeProperties = (
  flightItinery: FlightItineraryApi
): AmplitudeFlightSlicePriceProps => {
  const flightSliceProps: {
    [key: string]: AmplitudeFlightSliceApiProps
  } = {}
  flightItinery.flight_slices.forEach((flightSlice, ix) => {
    const key = `[${ix}]`
    flightSliceProps[key] = flightSliceApiToAmplitudeProperties(flightSlice)
  })
  const flightPriceProps = flightPriceToAmplitudeProperties(
    flightItinery.properties.price
  )
  return {
    slices: { ...flightSliceProps },
    ...flightPriceProps,
  }
}

export const flightItinerarySummaryToAmplitudeProperties = (
  flightItinery: FlightItinerarySummary
): AmplitudeFlightSlicePriceProps => {
  const flightSliceProps: {
    [key: string]: AmplitudeFlightSliceApiProps
  } = {}
  flightItinery.flight_slices.forEach((flightSlice, ix) => {
    const key = `[${ix}]`
    flightSliceProps[key] = flightSliceApiToAmplitudeProperties(flightSlice)
  })
  const flightPriceProps = flightPriceToAmplitudeProperties(
    flightItinery.properties.price
  )
  return {
    slices: { ...flightSliceProps },
    ...flightPriceProps,
  }
}

export const itineraryEventToAmplitudeProperties = (
  event: ItineraryEvent
): { event: AmplitudeItineraryEventProps } => {
  let properties: AmplitudeItineraryEventProps = {
    id: event.id,
    start: timeStampToAmplitudeProperties(event.start_at, event.time_zone),
    end: timeStampToAmplitudeProperties(event.end_at, event.end_time_zone),
    allDay: event.all_day,
    title: event.title,
    time_zone: event.time_zone,
    type: event.type,
    botGenerated: event.bot_generated,
    ...travelerGroupToAmplitudeProperties(event.travelers),
  }
  if (event.place) {
    properties = {
      ...properties,
      ...placeToAmplitudeProperties(event.place),
    }
  }
  if (event.flight_itinerary) {
    properties = {
      ...properties,
      ...flightItinerarySummaryToAmplitudeProperties(event.flight_itinerary),
    }
  }
  if (event.hotel_booking) {
    properties = {
      ...properties,
      ...hotelBookingSummaryToAmplitudeProperties(event.hotel_booking),
    }
  }
  if (event.booked_hotel_rooms) {
    properties = {
      ...properties,
      ...bookedHotelRoomsToAmplitudeProperties(event.booked_hotel_rooms),
    }
  }
  return { event: properties }
}

export const undoResponseToAmplitudeProperties = (
  undoResponse: UndoRedoResponseAndMessage
): AmplitudeUndoResponseProps => {
  assert(
    undoResponse.response,
    "expected an undo response for amplitude properties"
  )
  const response = undoResponse.response
  // Only write out for the case of 1 undo.
  // Multiple undo of the itinerary generation we should skip (too much data)
  if (response.actions) {
    if (response.actions.length > 1) {
      return {
        operation: "multiple-undo",
        toastMessage: undoResponse.toastMessage,
      }
    } else if (response.actions.length === 1) {
      const firstAction = response.actions[0]
      return {
        operation: firstAction.operation,
        ...itineraryEventToAmplitudeProperties(firstAction.event),
        toastMessage: undoResponse.toastMessage,
      }
    } else {
      return {
        operation: "error-undo",
        toastMessage: response.error,
      }
    }
  } else {
    return {
      operation: "no-undo",
      toastMessage: "Nothing to undo",
    }
  }
}

export const redoResponseToAmplitudeProperties = (
  redoResponse: UndoRedoResponseAndMessage
): AmplitudeRedoResponseProps => {
  assert(
    redoResponse.response,
    "expected a redo response for amplitude properties"
  )
  const response = redoResponse.response
  // Only write out for the case of 1 redo.
  // Multiple redo of the itinerary generation we should skip (too much data)
  if (response.actions) {
    if (response.actions.length > 1) {
      return {
        operation: "multiple-redo",
        toastMessage: redoResponse.toastMessage,
      }
    } else if (response.actions.length === 1) {
      const firstAction = response.actions[0]
      return {
        operation: firstAction.operation,
        ...itineraryEventToAmplitudeProperties(firstAction.event),
        toastMessage: redoResponse.toastMessage,
      }
    } else {
      return {
        operation: "error-redo",
        toastMessage: response.error,
      }
    }
  } else {
    return {
      operation: "no-redo",
      toastMessage: "Nothing to redo",
    }
  }
}

export const feedBackDataToAmplitudeProperties = (
  feedBack: FeedbackData
): { feedBack: AmplitudeFeedbackProps } => {
  const comment = feedBack.comment ? feedBack.comment : undefined
  return {
    feedBack: {
      type: feedBack.type,
      thumbsUp: feedBack.thumbs_up ? "true" : "false",
      comment: comment,
      entityRef: feedBack.entity_ref,
    },
  }
}

export const availableRoomToAmplitudeProperties = (
  availableRoom: AvailableRoom,
  start: DayObject | undefined,
  end: DayObject | undefined
): { room: AmplitudeAvailableRoomProps } => {
  return {
    room: {
      title: availableRoom?.title,
      description: availableRoom?.description,
      bedConfigs: availableRoom?.bed_configs,
      distributionType: availableRoom?.distribution_type,
      paymentType: availableRoom?.payment_type,
      boardType: availableRoom?.board_type,
      refundType: availableRoom?.refund_type,
      occupancyLimit: availableRoom?.occupancy_limit,
      availableCount: availableRoom?.available_count,
      checkIn: start,
      checkOut: end,
      ...hotelPriceDetailsToAmplitudeProperties(availableRoom?.price_details),
    },
  }
}

export const availableRoomHotelBookingToAmplitudeProperties = (
  availableRoom: AvailableRoom
): { hotelBooking: Pick<AmplitudeHotelBookingProps, "provider_name"> } => {
  return {
    hotelBooking: {
      provider_name: availableRoom?.provider,
    },
  }
}

export const hotelPriceDetailsToAmplitudeProperties = (
  priceDetails: PriceDetails
): { price: AmplitudeHotelPriceDetails } => {
  return {
    price: {
      displayCurrency: priceDetails.display_currency,
      displayPrice: priceDetails.display_price,
      displaySubTotal: priceDetails.display_sub_total,
      displayPropertyFee: priceDetails.display_property_fee,
      displayInsuranceFee: priceDetails.display_insurance_fee,
      displayTaxes: priceDetails.display_taxes,
      displayTotal: priceDetails.display_total,
      nights: priceDetails.nights,
    },
  }
}

export const roomDataToAmplitudeProperties = (
  room: HotelBookingVendorData | AvailableRoom
): { room: AmplitudeVendorDataProps } => {
  return {
    room: {
      title: room.title,
      description: room.description,
      ...hotelPriceDetailsToAmplitudeProperties(room.price_details),
    },
  }
}

export const airportSummaryToAmplitudeProperties = (
  airportSummary: AirportSummary
): AmplitudeAirportSummaryProps => {
  return {
    airport: {
      name: `${airportSummary.entity_ref}|${airportSummary.code}|${airportSummary.name}`,
    },
  }
}

// Don't send this out for.  Too much data
// export const flightLegsToAmplitudeProperties = (flightLegs: FlightLeg[]) => {
//   const flightLegProps: AmplitudeFlightLegsProps = {}
//
//   flightLegs.forEach((flightLeg, ix) => {
//     const key = `[${ix}]`
//     flightLegProps[key] = {
//       flightNumber: `${flightLeg.airline_iata} ${flightLeg.flight_number}`,
//     }
//     if (flightLeg.departure_airport) {
//       flightLegProps[key] = {
//         ...flightLegProps[key],
//         dep: AirportSummaryToAmplitudeProperties(flightLeg.departure_airport),
//       }
//     }
//     if (flightLeg.arrival_airport) {
//       flightLegProps[key] = {
//         ...flightLegProps[key],
//         arr: AirportSummaryToAmplitudeProperties(flightLeg.arrival_airport),
//       }
//     }
//   })
//   return {
//     legs: { ...flightLegProps },
//   }
// }

export const flightOptionToAmplitudeProperties = (flightOption: {
  trip_type: FlightOption["trip_type"]
  cheapest?: FlightOption["cheapest"]
  fastest?: FlightOption["fastest"]
  favorite: FlightOption["favorite"]
  price: FlightOption["price"]
  flight_slice: FlightOption["flight_slice"]
}): AmplitudeFlightOptionProps => {
  return {
    flight: {
      tripType: flightOption.trip_type,
      cheapest: flightOption.cheapest,
      fastest: flightOption.fastest,
      favorite: flightOption.favorite,
      price: flightOption.price,
      ...flightSliceApiToAmplitudeProperties(flightOption.flight_slice),
    },
  }
}

export const flightUpgradeToAmplitudeProperties = (
  upgrade: FlightContractUpgrade
): { upgrade: AmplitudeFlightUpgradeProps } => {
  return {
    upgrade: {
      name: upgrade.name,
      label: upgrade.label,
      price: upgrade.price,
      priceDiff: upgrade.price_diff,
    },
  }
}

export const flightPriceToAmplitudeProperties = (
  price: AmplitudeFlightPriceProps
): { price: AmplitudeFlightPriceProps } => {
  return {
    price: price,
  }
}

export const flightBookingToAmplitudeProperties = (
  flightBooking: FlightBooking
): AmplitudeFlightBookingProps => {
  let properties: AmplitudeFlightBookingProps = {
    status: flightBooking.status,
  }
  if (flightBooking.id) {
    properties = {
      ...properties,
      id: flightBooking.id,
    }
  }
  if (flightBooking.flight_itinerary) {
    properties = {
      ...properties,
      ...flightItineraryToAmplitudeProperties(flightBooking.flight_itinerary),
    }
  }
  return properties
}

export const flightItineraryToAmplitudeProperties = (
  flightItinerary: FlightItinerary
): { flight: AmplitudeFlightItineraryProps } => {
  const flightSliceProps: {
    [key: string]: AmplitudeFlightSliceProps
  } = {}
  flightItinerary.flight_slices?.forEach((flightSlice, ix) => {
    const key = `[${ix}]`
    flightSliceProps[key] = flightSliceToAmplitudeProperties(flightSlice)
  })

  return {
    flight: {
      id: flightItinerary.id,
      tripType: flightItinerary.trip_type,
      adults: flightItinerary.properties.adults,
      children: flightItinerary.properties.children,
      slices: flightSliceProps,
      ...flightPriceToAmplitudeProperties(flightItinerary.properties.price),
    },
  }
}

export const flightSliceToAmplitudeProperties = (
  flightSlice: FlightSlice
): AmplitudeFlightSliceProps => {
  let properties: {
    id: Nullable<number>
    stop: number
    depAt?: AmplitudeDayTimeProps
    dep?: AmplitudeAirportSummaryProps
    arrAt?: AmplitudeDayTimeProps
    arr?: AmplitudeAirportSummaryProps
    legs?: AmplitudeFlightLegsProps
  } = {
    id: flightSlice.id,
    stop: flightSlice.stops,
  }
  if (flightSlice.departure_airport) {
    properties = {
      ...properties,
      depAt: timeStampToAmplitudeProperties(
        flightSlice.departs_at,
        flightSlice.departure_airport.time_zone
      ),
      dep: airportSummaryToAmplitudeProperties(flightSlice.departure_airport),
    }
  }
  if (flightSlice.arrival_airport) {
    properties = {
      ...properties,
      arrAt: timeStampToAmplitudeProperties(
        flightSlice.arrives_at,
        flightSlice.arrival_airport.time_zone
      ),
      arr: airportSummaryToAmplitudeProperties(flightSlice.arrival_airport),
    }
  }
  // if (flightSlice.flight_legs) {
  //   properties = {
  //     ...properties,
  //     ...flightLegsToAmplitudeProperties(flightSlice.flight_legs),
  //   }
  // }
  return properties
}

export const flightSliceApiToAmplitudeProperties = (
  flightSlice: FlightSliceApi
): AmplitudeFlightSliceApiProps => {
  return {
    id: flightSlice.id,
    stop: flightSlice.stops,
    dep: airportSummaryToAmplitudeProperties(flightSlice.departure_airport),
    depAt: timeStampToAmplitudeProperties(
      flightSlice.departs_at,
      flightSlice.departure_airport.time_zone
    ),
    arr: airportSummaryToAmplitudeProperties(flightSlice.arrival_airport),
    arrAt: timeStampToAmplitudeProperties(
      flightSlice.arrives_at,
      flightSlice.arrival_airport.time_zone
    ),
    // ...flightLegsToAmplitudeProperties(flightSlice.flight_legs),
  }
}

export const errorToAmplitudeProperties = (error: string) => {
  return {
    error: error,
  }
}

export const eventActorToAmplitudeProperties = (
  eventActor: EventActor
): AmplitudeEventActorProps => {
  return {
    entity_ref: eventActor.entity_ref,
    name: eventActor.name,
  }
}

export const inspirationToAmplitudeProperties = (
  inspiration: Inspiration
): AmplitudeInspirationProps => {
  return {
    entity_ref: inspiration.entity_ref,
    handle: inspiration.owner.handle,
    days: inspiration.total_days,
    place: inspiration.location
      ? fullLocationName(inspiration.location)
      : AmplitudeEmptyValue,
    title: inspiration.title,
    template: inspiration.template,
    status: inspiration.status,
    is_from_thatch: inspiration.is_from_thatch,
  }
}

export const inspirationUpdateToAmplitudeProperties = (
  inspiration: Inspiration,
  where: InspirationWhere,
  dayNum?: number,
  block_type?: string
): AmplitudeInspirationUpdateProps => {
  const ret = inspirationToAmplitudeProperties(inspiration)
  return { ...ret, day_num: dayNum, where: where, block_type: block_type }
}

export const inspirationItemToAmplitudeProperties = (
  item: InspirationItem
): AmplitudeInspirationItemProps => {
  return {
    id: item.id,
    title: item.title,
    block_type: item.block_type,
    place: item.detailed ? item.place?.name : "",
  }
}

export const inspirationItemUpdateToAmplitudeProperties = (
  item: InspirationItem,
  where: InspirationWhere
): AmplitudeInspirationItemUpdateProps => {
  const ret = inspirationItemToAmplitudeProperties(item)
  return { ...ret, where: where }
}

export const collectionToAmplitudeProperties = (
  collection: Collection
): AmplitudeCollectionProps => {
  return {
    ref: collection.ref,
    name: collection.name,
    public: collection.public,
  }
}

export const attachmentMetaDataToAmplitudeProperties = (
  metaData: AttachmentMetadata
): AmplitudeAttachmentMetaDataProps => {
  return {
    title: metaData.title,
    description: metaData.description,
    thumbnailUrl: metaData.thumbnail_url,
    handle: metaData.handle,
  }
}

export const chatAttachmentToAmplitudeProperties = (
  attachment: Attachment
): AmplitudeAttachmentProps => {
  let ret: AmplitudeAttachmentProps = {
    type: attachment.type,
    url: attachment.url,
    dataType: attachment.data_type,
  }

  if (attachment.metadata) {
    ret = {
      ...ret,
      metaData: attachmentMetaDataToAmplitudeProperties(attachment.metadata),
    }
  }
  return ret
}

export const exploreFilterToAmplitudeProperties = (
  filters: ExploreFilters
): AmplitudeExploreFilterProps => {
  // We are just returning everything.
  return { filter: JSON.stringify(filters) }
}

export const communityFilterToAmpltidueProperties = (
  community: SocialFilters["type"]
): AmplitudeCommunityFilterProps => {
  return community ? community : AmplitudeEmptyValue
}

export const notificationClickToAmplitudeProperties = (
  notification: Notification,
  surface: "toast" | "list"
): EventNotificationClickProperties["properties"] => {
  const properties: EventNotificationClickProperties["properties"] = {
    surface: surface === "list" ? "notification_list" : "in_app_toast",
    type: notification.type,
    durability: notification.durability,
  }

  if ("id" in notification) {
    properties.notification_id = notification.id
    if (surface === "list") {
      properties.already_read = notification.read_at != null
    }
  }
  if ("trip_ref" in notification && notification.trip_ref) {
    properties.trip_ref = notification.trip_ref
  }
  if ("chat_id" in notification) {
    properties.chat_id = notification.chat_id
  }
  const collectionRef =
    ("collection" in notification ? notification.collection?.ref : undefined) ??
    ("collection_ref" in notification ? notification.collection_ref : undefined)
  if (collectionRef) {
    properties.collection_ref = collectionRef
  }
  if ("proactive_agent_id" in notification) {
    properties.proactive_agent_id = notification.proactive_agent_id
  }

  return properties
}
